import React, { useEffect, useState } from "react";
import {
    Card,
    Stack,
    Container,
    Typography,
    TableContainer,
    CardContent,
    Box,
    CircularProgress,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Accordion,
    AccordionSummary,
    AccordionDetails, Button,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Route } from "react-router-dom";

import copyObject from "../utils/copyObject";

import { GeneralLayout } from "../layouts/GeneralLayout";
import PageTitle from "../components/PageTitle";
import { useGetFacultiesQuery } from "../store/faculty/facultyApi";

export default function AttestationsUMUPage() {
    const { data: facultiesData } = useGetFacultiesQuery();
    const [faculties, setFaculties] = useState([]);
    const [groups, setGroups] = useState({});
    const [loading, setLoading] = useState(true);
    const [expandedFaculty, setExpandedFaculty] = useState(null);
    const [loadingGroups, setLoadingGroups] = useState({});

    const fetchGroups = async (facultyId) => {
        try {
            const response = await fetch(`/Attestation/GetFacultyGroupsWithLastAttestations?facultyId=${facultyId}&semesterId=${localStorage.getItem("selectedSemesterId")}`,
                {
                    method: "GET",
                    headers: {
                        "semesterid": localStorage.getItem("selectedSemesterId"),
                        "Content-Type": "application/json",
                        "Authorization": `Bearer ${localStorage.getItem('token')}`
                    }
                });
            const data = await response.json();

            return data.sort((l,r) => l.groupName > r.groupName ? 1 : -1).map(group => ({
                key: group.groupId.toString(),
                value: group.groupName,
                attestation: group.attestation
            }));
        } catch (error) {
            console.error(`Error loading groups for faculty ${facultyId}:`, error);
            return [];
        }
    };

    useEffect(() => {
        if (facultiesData && faculties.length === 0) {
            setFaculties(copyObject(facultiesData.data));
            setLoading(false);
        }
    }, [facultiesData]);

    const handleAccordionChange = async (facultyId) => {
        if (expandedFaculty === facultyId) {
            setExpandedFaculty(null);
            return;
        }

        setExpandedFaculty(facultyId);

        if (!groups[facultyId]) {
            try {
                setLoadingGroups(prev => ({ ...prev, [facultyId]: true }));
                const groupsData = await fetchGroups(facultyId);
                setGroups(prev => ({ ...prev, [facultyId]: groupsData }));
            } catch (error) {
                console.error(`Error loading groups for faculty ${facultyId}:`, error);
            } finally {
                setLoadingGroups(prev => ({ ...prev, [facultyId]: false }));
            }
        }
    };

    if (loading) {
        return (
            <GeneralLayout>
                <Container>
                    <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                        <CircularProgress />
                    </Box>
                </Container>
            </GeneralLayout>
        );
    }

    return (
        <GeneralLayout>
            <PageTitle title={`Посещаемость по группам`} />

            <Container>
                <Stack direction="column" padding="120px 0px 0px 100px" spacing={2}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
                        <Typography variant="h4" gutterBottom>
                            Аттестации
                        </Typography>
                    </Stack>

                    <Card>
                        <CardContent>
                            {faculties.length === 0 ? (
                                <Typography>Нет доступных факультетов</Typography>
                            ) : (
                                <Stack spacing={2}>
                                    {faculties.map((faculty) => (
                                        <Accordion
                                            key={faculty.key}
                                            expanded={expandedFaculty === faculty.key}
                                            onChange={() => handleAccordionChange(faculty.key)}
                                        >
                                            <AccordionSummary
                                                expandIcon={<ExpandMoreIcon />}
                                                aria-controls={`${faculty.key}-content`}
                                                id={`${faculty.key}-header`}
                                            >
                                                <Typography variant="subtitle1">
                                                    {faculty.value}
                                                </Typography>
                                            </AccordionSummary>
                                            <AccordionDetails>
                                                {loadingGroups[faculty.key] ? (
                                                    <Box display="flex" justifyContent="center" p={2}>
                                                        <CircularProgress size={24} />
                                                    </Box>
                                                ) : groups[faculty.key] && groups[faculty.key].length > 0 ? (
                                                    <TableContainer>
                                                        <Table size="small">
                                                            <TableHead>
                                                                <TableRow>
                                                                    <TableCell>Группа</TableCell>
                                                                    <TableCell>Период аттестации</TableCell>
                                                                    <TableCell>Ссылка</TableCell>
                                                                    <TableCell>Заполнено</TableCell>
                                                                </TableRow>
                                                            </TableHead>
                                                            <TableBody>
                                                                {groups[faculty.key].map((group) => (
                                                                    <TableRow key={group.key}>
                                                                        <TableCell>
                                                                            {group.value}
                                                                        </TableCell>
                                                                        <TableCell>
                                                                            {group.attestation ?
                                                                                `${new Date(group.attestation.validFrom).toLocaleDateString()} - ${new Date(group.attestation.validTo).toLocaleDateString()}`
                                                                                : 'Нет данных'
                                                                            }
                                                                        </TableCell>
                                                                        <TableCell>
                                                                            <Button
                                                                                href={`/attestationdetails/${group.attestation.attestationId}`}
                                                                                style={{
                                                                                    textDecoration: 'none',
                                                                                    color: '#1976d2',
                                                                                    fontWeight: 'bold'
                                                                                }}
                                                                                target="_blank"
                                                                            >
                                                                                Ссылка на ведомость
                                                                            </Button>
                                                                        </TableCell>
                                                                        <TableCell>
                                                                            {group.attestation.completionPercentage.toFixed(2)}%
                                                                        </TableCell>
                                                                    </TableRow>
                                                                ))}
                                                            </TableBody>
                                                        </Table>
                                                    </TableContainer>
                                                ) : (
                                                    <Typography variant="body2" color="textSecondary">
                                                        Нет доступных групп
                                                    </Typography>
                                                )}
                                            </AccordionDetails>
                                        </Accordion>
                                    ))}
                                </Stack>
                            )}
                        </CardContent>
                    </Card>

                    <br />
                </Stack>
            </Container>
        </GeneralLayout>
    );
}